import { useQueryCore } from "@/hooks/useQueryCore";
import { getPrayers, getPeople } from "@/lib/api";
import { supabase } from "@/lib/supabase";

/**
 * useLinkedPrayers — stale-while-revalidate hook for LinkedPrayers component.
 *
 * Replaces raw Supabase fetch + useState/useEffect with a cached, offline-aware
 * data layer. Both cases (personId / familyId) share the same memory+Dexie cache
 * infrastructure so revisits and page reloads paint instantly.
 *
 * Person case:  delegates to getPrayers({ person_id }) — gains outbox merge and
 *               api.js offline fallback on top of useQueryCore's Dexie Phase 0.
 *
 * Family case:  uses the same OR-filter (family_id = X OR person_id IN members)
 *               as before, but wrapped in useQueryCore so results are cached.
 *               People fetch goes through getPeople (api.js + Dexie) — the result
 *               is usually already in Dexie from useFamilyDetail's prior load.
 *
 * Query keys:
 *   ["linked-prayers", "person", personId]
 *   ["linked-prayers", "family", familyId]
 *
 * These keys are intentionally separate from ["prayers", districtId, filter]
 * used by useVisits/Prayers.jsx so they don't conflict. EntityHistory's future
 * migration can align on ["linked-prayers", ...] to share the same cache entry.
 */
export function useLinkedPrayers({ personId, familyId }) {
  const key = personId
    ? ["linked-prayers", "person", personId]
    : familyId
    ? ["linked-prayers", "family", familyId]
    : ["linked-prayers", null];

  const cacheKey = personId
    ? `linked_prayers_person_${personId}`
    : familyId
    ? `linked_prayers_family_${familyId}`
    : null;

  const { data, loading, error } = useQueryCore({
    key,
    enabled: !!(personId || familyId),
    queryFn: async () => {
      // ── Person case ───────────────────────────────────────────────────────
      // getPrayers handles: offline fallback, Dexie cache, outbox merge.
      if (personId) {
        return getPrayers({ person_id: personId });
      }

      // ── Family case ───────────────────────────────────────────────────────
      // Step 1: get people via api.js (Dexie-cached; usually already warm from
      // useFamilyDetail's prior getPeople call on the same family).
      const people = await getPeople({ family_id: familyId });
      const personIds = (people || []).map((p) => p.id);
      const nameMap = Object.fromEntries(
        (people || []).map((p) => [p.id, p.name])
      );

      // Step 2: OR-filter — prayers linked to family OR to any family member.
      // This query shape can't be expressed through getPrayers (AND-only filters),
      // so we use Supabase directly here. useQueryCore's Dexie Phase 0 covers
      // offline reads of the compound result; Phase 1 silently refreshes online.
      let query = supabase
        .from("prayers")
        .select("*")
        .order("created_at", { ascending: false });

      if (personIds.length > 0) {
        query = query.or(
          `family_id.eq.${familyId},person_id.in.(${personIds.join(",")})`
        );
      } else {
        query = query.eq("family_id", familyId);
      }

      const { data: rows, error: prayerError } = await query;
      if (prayerError) throw prayerError;

      // Enrich with person_name for display (shown only in family context).
      return (rows || []).map((p) => ({
        ...p,
        person_name: nameMap[p.person_id] || null,
      }));
    },
    cacheKey,
  });

  return {
    prayers: Array.isArray(data) ? data : [],
    loading,
    error,
  };
}
